import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import {
    TankContainerStyle,
    TankDetailsRows,
    TankDetailsRow,
    TankStyle,
    TankDetailsCenter,
    TankDetailsCenterRow,
    TankDetailsText,
    WarningTriangle,
    YellowTriangle,
    RedTriangle,
    OperationTypeIndicator,
    TankCurtain,
    TankFillingMenu,
    TankInfoMenu,
    FillWrapper,
    CargoInfoMenu,
    BlockedTankStyle,
    TankDetailsCargoNumbers,
    TankZoomMenu,
    ProgressBarContainer,
    ProgressBarOuter,
    ProgressBarInner,
} from './TankPlanViewer.styles';
import { getCoatingName } from 'src/utils/stowage/tankUtils';
import { getCargoColor } from 'src/utils/stowage/cargoColorsUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useStowageStore } from 'src/context/StowageContext';
import { undoStowageForCargoAndTank } from 'src/utils/stowage/stowageOperation/undoStowageUtils';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { useRotationStore } from 'src/context/RotationContext';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { roundTo0Decimal } from 'src/utils/numberHelper';
import { useAuthStore } from 'src/context/AuthContext';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { TankVisualProps } from 'src/types/ViewModel/Vessel/TankVisualProps';
import { OverlayMenuEnum, OverlayMenuState } from 'src/types/ViewModel/Vessel/TankPlan';
import { isTankBlocked } from 'src/utils/stowage/stowageOperation/blockedTankUtils';
import CargoNumber from '../common/cargoNumber';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import useStowageApi from 'src/api/stowage/usePostStowageApi';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { canFillTanks } from 'src/utils/stowage/stowageOperation/fillTankUtils';
import { deepCopyStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';
import { createCustomDragImage } from '../operationListView/operations/createCustomDragImage';
import { useToast } from 'src/app/useToast';

const TankVisual: React.FC<{ vm: TankVisualProps }> = ({ vm }) => {
    const containerMinimumHeights = [40, 55, 60, 90, 110];
    const [showTankFillingMenu, setShowTankFillingMenu] = useState(false);
    const [showCargoInfoMenu, setShowCargoInfoMenu] = useState(false);
    const [showTankInfoMenu, setShowTankInfoMenu] = useState(false);
    const [dragTooltip, setDragTooltip] = useState<{ visible: boolean; message: string; x: number; y: number }>({
        visible: false,
        message: '',
        x: 0,
        y: 0,
    });
    const dragTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const latestCursorPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const { role } = useAuthStore();
    const {
        setIsFillPanelOpen,
        tankPlanWindowResized,
        setOverlayMenuState,
        activeStowagePlan,
        setZoomedTank,
        setMouseClickPosition,
        unavailableTanks,
        setUnavailableTanks,
        unavailableTankViolations,
    } = useStowageStore();
    const { selectedCargoOperation, setHoveredTank, setSelectedCargoOperation } = useStowageSelectionStore();
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall, stowageRotationTree } = useRotationStore();
    const { saveStowageData } = useStowageApi();
    const { toastError } = useToast();

    // Helper function to get violation message for a tank
    const getViolationMessage = (tank: Tank): string | null => {
        const isTankUnavailable = unavailableTanks?.some((t) => t.tankId === tank.tankId);
        if (isTankUnavailable) {
            const violations = unavailableTankViolations?.get(tank.tankId);
            if (violations && violations.length > 0) {
                const uniqueDescriptions = Array.from(new Set(violations.map((v) => v.description)));
                return uniqueDescriptions.join(', ');
            }
            return 'Stowage rule violation';
        }

        const isTankBlockedForOperation = isTankBlocked(
            activeStowagePlan,
            tank,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            stowageRotationTree
        );
        if (isTankBlockedForOperation) {
            return 'Tank is blocked';
        }

        return null;
    };

    // Cleanup tooltip timeout on unmount
    useEffect(() => {
        return () => {
            if (dragTooltipTimeoutRef.current) {
                clearTimeout(dragTooltipTimeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        setShowTankFillingMenu(true);
        setShowTankInfoMenu(true);
        setShowCargoInfoMenu(true);
        setHoveredTank(vm.tank);
    };

    const handleMouseLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setShowTankFillingMenu(false);
        setShowTankInfoMenu(false);
        setShowCargoInfoMenu(false);
        setHoveredTank(null);
    };

    const [containerHeight, setContainerHeight] = useState<number>(0);

    useEffect(() => {
        // Reference to resize observer for cleanup
        let resizeObserver: ResizeObserver | null = null;

        const handleResize = () => {
            // Use requestAnimationFrame to ensure the calculation happens after browser paint
            requestAnimationFrame(() => {
                const tankElement = document.getElementById(`tank-${vm.tank.tankId}`);
                if (tankElement) {
                    const height = tankElement.offsetHeight;
                    if (height > 0) {
                        setContainerHeight(height);
                    }
                }
            });
        };

        // Setup ResizeObserver for more accurate size tracking
        const tankElement = document.getElementById(`tank-${vm.tank.tankId}`);
        if (tankElement) {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(tankElement);

            // Trigger initial resize calculation
            handleResize();
        }

        // Also keep window resize listener for global layout changes
        window.addEventListener('resize', handleResize);

        // Force a resize calculation on component mount and when dependencies change
        handleResize();

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [vm.tank.tankId, tankPlanWindowResized]);

    const onUndoStowage = (tank: Tank, cargo: RotationCargo) => {
        if (!activeStowagePlan || !cargo) {
            return;
        }
        const _stowagePlan = deepCopyStowagePlan(activeStowagePlan);
        const stowagePlanChanges = undoStowageForCargoAndTank(_stowagePlan, cargo, tank);
        saveStowageData(_stowagePlan, stowagePlanChanges);
    };

    const handleOnDrag = (e: React.DragEvent) => {
        if (vm.canDrag()) {
            if (vm.cargoContents?.length > 0 && vm.cargoContents?.[0]?.cargo) {
                createCustomDragImage(vm.cargoContents?.[0]?.cargo, vm.cargoContents?.[0]?.cargoOperation?.operationType ?? 'Transit', e);
            }
            if (selectedCargoOperation && !vm.cargoContents?.[0]?.cargoOperation) {
                setSelectedCargoOperation(null);
            }
            e.dataTransfer.setData('stowageGuid', vm.cargoContents?.[0]?.stowage?.stowageGuid ?? '');
            const operationType = vm.getDragOperationType() || 'Transit';
            e.dataTransfer.setData('dragOperationType', operationType);
        }
    };

    const handleOnDragOver = (e: React.DragEvent) => {
        if (vm.canDrop()) {
            e.preventDefault();
            e.stopPropagation();

            // Use selectedCargoOperation instead of dataTransfer.getData()
            // because browsers restrict access to dataTransfer data during dragover for security
            const dragOperationType = selectedCargoOperation?.operationType;

            // Check if tank is in the unavailable tanks list (from stowage rules validation)
            const isTankUnavailable = unavailableTanks?.some((t) => t.tankId === vm.tank.tankId);

            // Check if tank is blocked
            const isTankBlockedForOperation = isTankBlocked(
                activeStowagePlan,
                vm.tank,
                selectedStowageRotationPortCall,
                selectedStowageRotationBerthCall,
                stowageRotationTree
            );

            if (isTankUnavailable || isTankBlockedForOperation) {
                // Still allow drop event to fire so we can show a toast message explaining why
                e.dataTransfer.dropEffect = 'move';
                (e.currentTarget as HTMLElement).style.cursor = 'not-allowed';

                // Store latest cursor position in ref (below the cursor)
                latestCursorPosRef.current = { x: e.clientX, y: e.clientY + 60 };

                if (dragTooltip.visible) {
                    // Tooltip already visible - update position to follow cursor
                    setDragTooltip((prev) => ({
                        ...prev,
                        x: latestCursorPosRef.current.x,
                        y: latestCursorPosRef.current.y,
                    }));
                } else if (!dragTooltipTimeoutRef.current) {
                    // Start delay timer to show tooltip
                    dragTooltipTimeoutRef.current = setTimeout(() => {
                        const message = getViolationMessage(vm.tank);
                        if (message) {
                            // Read from ref to get latest cursor position
                            setDragTooltip({
                                visible: true,
                                message,
                                x: latestCursorPosRef.current.x,
                                y: latestCursorPosRef.current.y,
                            });
                        }
                        dragTooltipTimeoutRef.current = null;
                    }, 500); // 500ms delay
                }
                return;
            }

            // Clear tooltip if hovering over a valid tank
            if (dragTooltipTimeoutRef.current) {
                clearTimeout(dragTooltipTimeoutRef.current);
                dragTooltipTimeoutRef.current = null;
            }
            if (dragTooltip.visible) {
                setDragTooltip({ visible: false, message: '', x: 0, y: 0 });
            }

            // Set different cursor types based on operation type for better UX
            if (dragOperationType === 'Loading') {
                e.dataTransfer.dropEffect = 'copy';
                (e.currentTarget as HTMLElement).style.cursor = 'copy'; // Loading cargo into tank
            } else if (dragOperationType === 'Unloading') {
                // For unloading, only allow drops on tanks that contain the specific cargo
                const tankContainsSelectedCargo = vm.cargoes?.some((cargo) => cargo.cargoGuid === selectedCargoOperation?.cargoGuid);

                if (tankContainsSelectedCargo) {
                    e.dataTransfer.dropEffect = 'move';
                    (e.currentTarget as HTMLElement).style.cursor = 'alias'; // Unloading from tank
                } else {
                    e.dataTransfer.dropEffect = 'none';
                    (e.currentTarget as HTMLElement).style.cursor = 'not-allowed'; // Cannot unload from tank without the cargo
                    return;
                }
            } else if (dragOperationType === 'Transfer') {
                e.dataTransfer.dropEffect = 'copy';
                (e.currentTarget as HTMLElement).style.cursor = 'move'; // Transferring between tanks
            } else {
                // Fallback for empty or unknown operation types
                console.warn('Unknown or empty dragOperationType:', dragOperationType);
                e.dataTransfer.dropEffect = 'move';
                (e.currentTarget as HTMLElement).style.cursor = 'copy'; // Default to copy cursor
            }
        } else {
            // Tank cannot accept drops
            e.dataTransfer.dropEffect = 'none';
            (e.currentTarget as HTMLElement).style.cursor = 'not-allowed';
        }
    };

    const handleOnDragLeave = (e: React.DragEvent) => {
        // Reset cursor when leaving the tank
        (e.currentTarget as HTMLElement).style.cursor = 'default';

        // Clear tooltip and timeout
        if (dragTooltipTimeoutRef.current) {
            clearTimeout(dragTooltipTimeoutRef.current);
            dragTooltipTimeoutRef.current = null;
        }
        if (dragTooltip.visible) {
            setDragTooltip({ visible: false, message: '', x: 0, y: 0 });
        }
    };

    const handleOnDrop = (e: React.DragEvent, tank: Tank) => {
        e.preventDefault();
        e.stopPropagation();

        // Clear any tooltip on drop
        if (dragTooltipTimeoutRef.current) {
            clearTimeout(dragTooltipTimeoutRef.current);
            dragTooltipTimeoutRef.current = null;
        }
        if (dragTooltip.visible) {
            setDragTooltip({ visible: false, message: '', x: 0, y: 0 });
        }

        // Check if tank is in the unavailable tanks list (from stowage rules validation)
        const isTankUnavailable = unavailableTanks?.some((t) => t.tankId === tank.tankId);
        if (isTankUnavailable) {
            // Show toast with violation reason(s)
            const violations = unavailableTankViolations?.get(tank.tankId);
            if (violations && violations.length > 0) {
                const uniqueDescriptions = Array.from(new Set(violations.map((v) => v.description)));
                const message = `Cannot stow in tank ${tank.tankName}: ${uniqueDescriptions.join(', ')}`;
                toastError(message, 6000);
            } else {
                toastError(`Cannot stow in tank ${tank.tankName}: stowage rule violation`);
            }
            return;
        }

        if (isTankBlocked(activeStowagePlan, tank, selectedStowageRotationPortCall, selectedStowageRotationBerthCall, stowageRotationTree)) {
            toastError(`Cannot stow in tank ${tank.tankName}: tank is blocked`);
            return;
        }

        // Note: DataTransfer API converts keys to lowercase in some browsers
        const dragOperationType = e.dataTransfer.getData('dragoperationtype') || e.dataTransfer.getData('dragOperationType');
        const stowageGuid = e.dataTransfer.getData('stowageguid');

        if (dragOperationType === 'Loading' || dragOperationType === 'Unloading') {
            if (stowageGuid) {
                const dropStowageInTankResponse = vm.onDropStowageInTank(stowageGuid);
                if (dropStowageInTankResponse) {
                    onOpenEditStowage(tank, dropStowageInTankResponse);
                }
            } else {
                if (vm.fillTankOrTanks(tank, selectedCargoOperation)) {
                    onOpenEditStowage(tank);
                    setUnavailableTanks([]); // Clear unavailable tanks after filling
                }
            }
        } else if (dragOperationType === 'Transfer') {
            const stowagePlanChanges = vm.onDropTransferToTank(stowageGuid);
            if (stowagePlanChanges?.length > 0) {
                saveStowageData(activeStowagePlan, stowagePlanChanges);
            }
        }
    };

    const onFillTanks = (event: any, tank: Tank) => {
        event.preventDefault();
        event.stopPropagation();
        if (vm.fillTankOrTanks(tank, selectedCargoOperation)) {
            onOpenEditStowage(tank);
            setUnavailableTanks([]); // Clear unavailable tanks after filling
        }
    };

    const getPerCentageFillVolume = () => {
        if (vm.fillVolume) {
            return roundTo0Decimal((vm.fillVolume / vm.tank.volume) * 100);
        } else {
            return 0;
        }
    };

    const formatWeight = (weight: number): string => {
        if (weight < 1 && weight > 0) {
            return weight.toFixed(2);
        }
        return roundTo0Decimal(weight).toString();
    };

    const hasCargoContent = (tank: Tank): boolean => {
        return tank && Boolean(vm.cargoContents?.length > 0);
    };

    const getOperationTypeLetter = (operationType: string) => {
        return operationType.charAt(0).toUpperCase();
    };

    const getCargoOperationTypes = () => {
        const cargoOperationTypeLetters = vm.cargoOperationTypes?.map(getOperationTypeLetter).join('/');
        if (cargoOperationTypeLetters) {
            return <OperationTypeIndicator>{cargoOperationTypeLetters}</OperationTypeIndicator>;
        } else {
            return <></>;
        }
    };

    const getViolationIcon = () => {
        return (
            <WarningTriangle>
                {vm.hardRuleViolations.length > 0 ? (
                    <RedTriangle hasBackground>
                        <FontAwesomeIcon icon="exclamation-triangle" size="3x" />
                    </RedTriangle>
                ) : vm.softRuleViolations.length > 0 ? (
                    <YellowTriangle hasBackground>
                        <FontAwesomeIcon icon="exclamation-triangle" size="3x" />
                    </YellowTriangle>
                ) : (
                    <></>
                )}
            </WarningTriangle>
        );
    };

    const onOpenEditStowage = (tank: Tank, additionalStowagePlanChanges?: StowagePlanChange[]) => {
        setOverlayMenuState({
            visible: true,
            type: OverlayMenuEnum.EditStowage,
            tank: tank,
            cargoContents: vm.cargoContents,
            stowagePlanChanges: additionalStowagePlanChanges,
        } as OverlayMenuState);
        setIsFillPanelOpen(true);
    };

    const getTankColor = (cargoes: RotationCargo[]): string => {
        if (!cargoes || cargoes.length === 0) {
            return null;
        }

        const color = getCargoColor(cargoes[0]);
        return color;
    };

    const handleZoomTank = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // If an external zoom handler is provided, use that first
        if (vm.onZoomClick) {
            vm.onZoomClick(e);
        } else {
            // Fall back to the default behavior
            setZoomedTank(vm.tank);
        }
    };

    return (
        <TankContainerStyle
            cofferdams={vm.cofferDams}
            draggable={vm.canDrag()}
            onDragStart={(e) => {
                handleOnDrag(e);
            }}
            onContextMenu={(e) => vm.onOpenTankMenu(e, vm.tank)} // Added right-click event listener
            onDragOver={(e) => handleOnDragOver(e)}
            onDragLeave={(e) => handleOnDragLeave(e)}
            onDrop={(e) => handleOnDrop(e, vm.tank)}
            onMouseEnter={(e) => handleMouseEnter(e)}
            onMouseLeave={(e) => handleMouseLeave(e)}
            id={`tank-${vm.tank.tankId}`}
            isZoomed={vm.isZoomed}
            isReport={vm.isReport}
        >
            <TankStyle
                onClick={(e) => vm.onClickTank(vm.tank, e)}
                color={getTankColor(vm.cargoes)}
                isDimmed={vm.isDimmed}
                isSelected={vm.isSelected}
                className={vm.isDimmed ? 'is-dimmed' : ''}
                isZoomed={vm.isZoomed}
                isReport={vm.isReport}
            >
                <TankDetailsRows>
                    <TankDetailsRow>
                        {(containerHeight > containerMinimumHeights[0] || vm.isZoomed) && (
                            <TankDetailsText>{`${vm.tank.tankName} (${getCoatingName(vm.tank.coatingType)})`}</TankDetailsText>
                        )}
                        {(containerHeight > containerMinimumHeights[0] || vm.isZoomed) && !vm.isReport && getCargoOperationTypes()}
                    </TankDetailsRow>
                    <TankDetailsCenter className="text-overflow">
                        {/* Violation icon in center */}
                        {!vm.isReport && (vm.hardRuleViolations.length > 0 || vm.softRuleViolations.length > 0) && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '70%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 10,
                                    pointerEvents: 'none',
                                }}
                            >
                                {getViolationIcon()}
                            </div>
                        )}
                        {(containerHeight > containerMinimumHeights[1] || vm.isZoomed) && vm.cargoes && (
                            <>
                                <TankDetailsRow className="cargo-number-row text-overflow">
                                    <TankDetailsCargoNumbers isZoomed={vm.isZoomed} isReport={vm.isReport}>
                                        {vm.cargoes.map((c, index) => {
                                            return (
                                                <li key={index}>
                                                    <CargoNumber
                                                        includeHash={true}
                                                        includeVoyage={false}
                                                        includeUscgNumber={true}
                                                        cargo={c}
                                                        includeColoredBackground={vm.cargoes.length > 0}
                                                        bold={true}
                                                        key={index}
                                                    />
                                                </li>
                                            );
                                        })}
                                    </TankDetailsCargoNumbers>
                                </TankDetailsRow>
                                <TankDetailsRow className="trade-name-row text-overflow">{vm.cargoes[0].product.tradeName}</TankDetailsRow>
                            </>
                        )}
                        {/* Update condition to display port names in zoom mode as well */}
                        {(containerHeight > containerMinimumHeights[4] || vm.isZoomed) &&
                            vm.cargoes?.length > 0 &&
                            vm.cargoes[0].loadingPortNames.length > 0 && (
                                <TankDetailsRow className="ports-row text-overflow">
                                    <TankDetailsText className="text-overflow">
                                        {vm.cargoes[0].loadingPortNames.length > 0 && (
                                            <span className="loading-port">{vm.cargoes[0].loadingPortNames[0].name}</span>
                                        )}
                                        {vm.cargoes[0].unloadingPortNames.length > 0 && (
                                            <span className="unloading-port">
                                                / {vm.cargoes[0].unloadingPortNames[vm.cargoes[0].unloadingPortNames.length - 1].name}
                                            </span>
                                        )}
                                    </TankDetailsText>
                                </TankDetailsRow>
                            )}
                    </TankDetailsCenter>

                    <TankDetailsRow>
                        <FillWrapper>
                            <TankDetailsText>
                                {(vm.fillVolume ? roundTo0Decimal(vm.fillVolume) : '0') + '/' + roundTo0Decimal(vm.tank.volumeAt98Percent)} m³
                            </TankDetailsText>
                            {(containerHeight > containerMinimumHeights[2] || vm.isZoomed) && (
                                <TankDetailsRow>
                                    <TankDetailsText
                                        className={`${vm.isShipFigureAdjusted ? 'adjusted-ship-figure' : ''} ${
                                            (vm.fillWeight ?? 0) < 1 && (vm.fillWeight ?? 0) > 0 ? 'small-amount' : ''
                                        }`.trim()}
                                    >
                                        {formatWeight(vm.fillWeight ?? 0)} MT
                                    </TankDetailsText>
                                    <TankDetailsText>{getPerCentageFillVolume()}%</TankDetailsText>
                                </TankDetailsRow>
                            )}
                        </FillWrapper>
                        <TankDetailsCenterRow bold maxOneLine isZoomed={vm.isZoomed}>
                            {!vm.isReport && containerHeight > containerMinimumHeights[2] && (
                                <ProgressBarContainer>
                                    <ProgressBarOuter>
                                        <ProgressBarInner percentage={getPerCentageFillVolume()} />
                                    </ProgressBarOuter>
                                </ProgressBarContainer>
                            )}
                        </TankDetailsCenterRow>
                    </TankDetailsRow>
                </TankDetailsRows>

                {showTankInfoMenu && !vm.isZoomed && !vm.isReport && (
                    <>
                        <TankCurtain />
                        <TankInfoMenu onClick={(e) => vm.onOpenTankMenu(e, vm.tank)} title="Open tank menu">
                            <FontAwesomeIcon icon="ellipsis-h" />
                        </TankInfoMenu>
                    </>
                )}
                {!vm.isBlocked &&
                    hasPermission(role, stowageFeatureEnum.EditStowage) &&
                    showTankFillingMenu &&
                    selectedCargoOperation &&
                    canFillTanks(activeStowagePlan, [vm.tank], selectedCargoOperation) &&
                    !hasCargoContent(vm.tank) &&
                    !vm.isZoomed &&
                    !vm.isReport && (
                        <>
                            <TankCurtain />
                            <TankFillingMenu onClick={(e) => onFillTanks(e, vm.tank)} title="Fill tank">
                                <FontAwesomeIcon icon="plus" />
                            </TankFillingMenu>
                        </>
                    )}
                {!vm.isBlocked &&
                    hasPermission(role, stowageFeatureEnum.EditStowage) &&
                    showTankFillingMenu &&
                    hasCargoContent(vm.tank) &&
                    vm.cargoes &&
                    !vm.isZoomed &&
                    !vm.isReport && (
                        <>
                            <TankCurtain />
                            <TankFillingMenu onClick={() => onUndoStowage(vm.tank, vm.cargoes[0])} title="Undo stowage">
                                <FontAwesomeIcon icon="minus" />
                            </TankFillingMenu>
                        </>
                    )}
                {!vm.isBlocked && showCargoInfoMenu && hasCargoContent(vm.tank) && !vm.isZoomed && !vm.isReport && (
                    <>
                        <TankCurtain />
                        <CargoInfoMenu
                            onClick={(e) => {
                                // Store the click position when opening the cargo details
                                setMouseClickPosition({
                                    top: e.clientY,
                                    left: e.clientX,
                                });
                                vm.onOpenCargoInfo(e, vm.tank);
                            }}
                            title="View cargo details"
                        >
                            <FontAwesomeIcon icon="info" />
                        </CargoInfoMenu>
                    </>
                )}
                {showTankInfoMenu && !vm.isZoomed && !vm.isReport && (
                    <>
                        <TankCurtain />
                        <TankZoomMenu onClick={handleZoomTank} title="Zoom tank view">
                            <FontAwesomeIcon icon="search-plus" />
                        </TankZoomMenu>
                    </>
                )}
                {vm.isBlocked && (
                    <BlockedTankStyle>
                        <div className="curtain"></div>
                        <FontAwesomeIcon className="ban-icon" icon="ban" />
                    </BlockedTankStyle>
                )}
            </TankStyle>
            {/* Drag tooltip portal */}
            {dragTooltip.visible &&
                ReactDOM.createPortal(
                    <div
                        style={{
                            position: 'fixed',
                            top: dragTooltip.y,
                            left: dragTooltip.x,
                            transform: 'translateX(-50%)',
                            backgroundColor: '#d32f2f',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            maxWidth: '300px',
                            zIndex: 10000,
                            pointerEvents: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {dragTooltip.message}
                    </div>,
                    document.body
                )}
        </TankContainerStyle>
    );
};

export default TankVisual;
